import apiClient from './api';

/**
 * Attendance Service - Handles all attendance, shift, late mark, and leave management APIs
 */
const attendanceService = {
  
  // ==================== LATE MARK REPORTING APIs ====================
  
  /**
   * Get all late marks with optional filters
   * @param {Object} filters - { employee_id, month (YYYY-MM), is_converted (true/false) }
   * @returns {Promise} - List of late marks
   */
  getAllLateMarks: async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      if (filters.employee_id) params.append('employee_id', filters.employee_id);
      if (filters.month) params.append('month', filters.month);
      if (filters.is_converted !== undefined) params.append('is_converted', filters.is_converted);
      
      const response = await apiClient.get(`/attendance/admin/late-marks?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching late marks:', error);
      throw error;
    }
  },

  /**
   * Get employee-specific late mark history with statistics
   * @param {number} employeeId - Employee ID
   * @param {Object} filters - { month (YYYY-MM), year (YYYY) }
   * @returns {Promise} - Employee late mark history and stats
   */
  getEmployeeLateMarks: async (employeeId, filters = {}) => {
    try {
      const params = new URLSearchParams();
      if (filters.month) params.append('month', filters.month);
      if (filters.year) params.append('year', filters.year);
      
      const response = await apiClient.get(
        `/attendance/admin/late-marks/employee/${employeeId}?${params.toString()}`
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching employee late marks:', error);
      throw error;
    }
  },

  /**
   * Get overall late mark statistics with breakdowns
   * @param {Object} filters - { month (YYYY-MM), year (YYYY) }
   * @returns {Promise} - Overall statistics, employee breakdown, monthly breakdown
   */
  getLateMarksStatistics: async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      if (filters.month) params.append('month', filters.month);
      if (filters.year) params.append('year', filters.year);
      
      const response = await apiClient.get(`/attendance/admin/late-marks/stats?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching late marks statistics:', error);
      throw error;
    }
  },

  /**
   * Get late mark conversion history (3 late marks -> 1 leave deduction)
   * @param {Object} filters - { month (YYYY-MM), employee_id }
   * @returns {Promise} - List of conversion events
   */
  getLateMarkConversions: async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      if (filters.month) params.append('month', filters.month);
      if (filters.employee_id) params.append('employee_id', filters.employee_id);
      
      const response = await apiClient.get(
        `/attendance/admin/late-marks/conversions?${params.toString()}`
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching conversion history:', error);
      throw error;
    }
  },

  // ==================== SHIFT MANAGEMENT APIs ====================

  /**
   * Get all work shifts
   * @returns {Promise} - List of work shifts
   */
  getAllShifts: async () => {
    try {
      const response = await apiClient.get('/attendance/admin/shifts');
      return response.data;
    } catch (error) {
      console.error('Error fetching shifts:', error);
      throw error;
    }
  },

  /**
   * Create a new work shift
   * @param {Object} shiftData - { name, start_time, end_time, grace_period_minutes, is_active }
   * @returns {Promise} - Created shift
   */
  createShift: async (shiftData) => {
    try {
      const response = await apiClient.post('/attendance/admin/shifts', shiftData);
      return response.data;
    } catch (error) {
      console.error('Error creating shift:', error);
      throw error;
    }
  },

  /**
   * Update an existing shift
   * @param {number} shiftId - Shift ID
   * @param {Object} shiftData - Updated shift data
   * @returns {Promise} - Updated shift
   */
  updateShift: async (shiftId, shiftData) => {
    try {
      const response = await apiClient.put(`/attendance/admin/shifts/${shiftId}`, shiftData);
      return response.data;
    } catch (error) {
      console.error('Error updating shift:', error);
      throw error;
    }
  },

  /**
   * Delete a shift
   * @param {number} shiftId - Shift ID
   * @returns {Promise}
   */
  deleteShift: async (shiftId) => {
    try {
      const response = await apiClient.delete(`/attendance/admin/shifts/${shiftId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting shift:', error);
      throw error;
    }
  },

  /**
   * Assign shift to employee
   * @param {number} employeeId - Employee ID
   * @param {number} shiftId - Shift ID
   * @returns {Promise} - Assignment details
   */
  assignShift: async (employeeId, shiftId, effectiveFrom, notes = '') => {
    try {
      const response = await apiClient.post('/attendance/admin/assign-shift', {
        employee_id: employeeId,
        shift_id: shiftId,
        effective_from: effectiveFrom,
        notes: notes
      });
      return response.data;
    } catch (error) {
      console.error('Error assigning shift:', error);
      throw error;
    }
  },

  /**
   * Get employee's current shift
   * @param {number} employeeId - Employee ID
   * @returns {Promise} - Employee shift details
   */
  getEmployeeShift: async (employeeId) => {
    try {
      const response = await apiClient.get(`/attendance/admin/employee-shift/${employeeId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching employee shift:', error);
      throw error;
    }
  },

  // ==================== LEAVE MANAGEMENT APIs ====================

  /**
   * Get all leave requests (with optional filters)
   * @param {Object} filters - { status, employee_id, month }
   * @returns {Promise} - List of leave requests
   */
  getAllLeaveRequests: async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.employee_id) params.append('employee_id', filters.employee_id);
      if (filters.month) params.append('month', filters.month);
      
      const response = await apiClient.get(`/attendance/admin/leave-requests?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching leave requests:', error);
      throw error;
    }
  },

  /**
   * Get pending leave requests
   * @returns {Promise} - List of pending leave requests
   */
  getPendingLeaveRequests: async () => {
    try {
      const response = await apiClient.get('/attendance/admin/leave-requests?status=pending');
      return response.data;
    } catch (error) {
      console.error('Error fetching pending leave requests:', error);
      throw error;
    }
  },

  /**
   * Approve a leave request
   * @param {number} requestId - Leave request ID
   * @param {string} adminNotes - Optional admin notes
   * @returns {Promise} - Updated leave request
   */
  approveLeaveRequest: async (requestId, adminNotes = '') => {
    try {
      const response = await apiClient.post(`/attendance/admin/leave-requests/${requestId}/approve`, {
        admin_notes: adminNotes
      });
      return response.data;
    } catch (error) {
      console.error('Error approving leave request:', error);
      throw error;
    }
  },

  /**
   * Reject a leave request
   * @param {number} requestId - Leave request ID
   * @param {string} adminNotes - Reason for rejection
   * @returns {Promise} - Updated leave request
   */
  rejectLeaveRequest: async (requestId, adminNotes) => {
    try {
      const response = await apiClient.post(`/attendance/admin/leave-requests/${requestId}/reject`, {
        admin_notes: adminNotes
      });
      return response.data;
    } catch (error) {
      console.error('Error rejecting leave request:', error);
      throw error;
    }
  },

  /**
   * Get leave balances for all employees
   * @returns {Promise} - List of employee leave balances
   */
  getAllLeaveBalances: async () => {
    try {
      const response = await apiClient.get('/attendance/admin/leave-balances');
      return response.data;
    } catch (error) {
      console.error('Error fetching leave balances:', error);
      throw error;
    }
  },

  /**
   * Initialize leave balances for an employee
   * @param {number} employeeId - Employee ID
   * @returns {Promise} - Created leave balances
   */
  initializeLeaveBalances: async (employeeId) => {
    try {
      const response = await apiClient.post('/attendance/admin/leave-balance/initialize', {
        employee_id: employeeId
      });
      return response.data;
    } catch (error) {
      console.error('Error initializing leave balances:', error);
      throw error;
    }
  }
};

export default attendanceService;
